// TODO: split into sub-components (ProfileForm, LogoUpload, ComplianceForm, DangerZone)
// Currently ~1,433 lines — refactor after launch.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import Card from "../common/Card";
import Button from "../common/Button";
import { useToast } from "../../hooks/useToast";
import { Eye, EyeOff } from "lucide-react";
import LogoUpload from "../common/LogoUpload";
import { friendlyAuthError } from "../../utils/friendlyAuthError";
import ThemeToggle from "../ThemeToggle";
import QuoteTemplatesPanel from "./QuoteTemplatesPanel";
import DirectLinkConfigPanel from "./DirectLinkConfigPanel";
import HowItWorksPanel from "./HowItWorksPanel";

import { TRADES as TRADE_TYPES } from "../../config/tradeOptions";

// ─── Shared layout tokens ──────────────────────────────────────────────────
const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "16px",
};

const DIVIDER = {
  borderTop: "1px solid var(--border, rgba(140,100,80,0.14))",
  margin: "22px 0",
};

// ─── Compliance field definitions ─────────────────────────────────────────

// Trade-specific compliance fields — union is computed from selected trades.
// Each entry: key (form field), label, type (text|date|toggle), required,
// placeholder, hint.
const TRADE_COMPLIANCE = {
  Electrical: [
    {
      key: "electrical_contractor_licence",
      label: "Electrical contractor licence number",
      placeholder: "State-issued licence no.",
      required: true,
      hint: "Mandatory in all states to contract electrical work.",
    },
    {
      key: "electrical_worker_licence",
      label: "Electrical worker licence number",
      hint: "Your personal worker licence — separate from the contractor licence.",
    },
    {
      key: "electrical_licence_class",
      label: "Licence class",
      placeholder: "e.g. Unrestricted, Restricted",
      hint: "Determines the scope of electrical work you are permitted to perform.",
    },
    {
      key: "issues_electrical_safety_cert",
      label: "I issue Certificates of Electrical Safety",
      type: "toggle",
      hint: "Required by law on completion of most electrical installations.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required for any work on a construction site.",
    },
  ],
  Plumbing: [
    {
      key: "plumbing_contractor_licence",
      label: "Plumbing contractor licence number",
      placeholder: "State-issued licence no.",
      required: true,
      hint: "Mandatory in all states to contract plumbing work.",
    },
    {
      key: "plumbing_licence_categories",
      label: "Licence categories held",
      placeholder: "e.g. Water, Drainage, Gas, Roofing",
      hint: "List all categories your licence covers.",
    },
    {
      key: "gas_fitting_licence",
      label: "Gas fitting licence number",
      hint: "Required if you perform gas work — separate from your plumbing licence in most states.",
    },
    {
      key: "issues_compliance_cert",
      label: "I issue Certificates of Compliance",
      type: "toggle",
      hint: "Required on completion of certain plumbing and drainage work.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required for any work on a construction site.",
    },
  ],
  Building: [
    {
      key: "builder_licence_number",
      label: "Builder licence number",
      placeholder: "e.g. QBCC 1234567",
      required: true,
      hint: "Mandatory for all domestic and commercial building work.",
    },
    {
      key: "builder_licence_class",
      label: "Licence class",
      placeholder: "e.g. Unlimited, Medium Rise, Low Rise",
      hint: "Determines the scale and type of building work you can legally perform.",
    },
    {
      key: "home_warranty_insurance",
      label: "I hold home warranty insurance",
      type: "toggle",
      hint: "Required in most states for residential work over $20,000.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required for any work on a construction site.",
    },
  ],
  Painting: [
    {
      key: "painter_licence_number",
      label: "Painter contractor licence number",
      hint: "Required in QLD, VIC and some other states — check your state authority.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required for any work on a construction site.",
    },
  ],
  Roofing: [
    {
      key: "roofer_licence_number",
      label: "Roofing contractor licence number",
      hint: "Required in most states — check your state licensing authority.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required for any work on a construction site.",
    },
    {
      key: "height_safety_training",
      label: "I hold height safety / working at heights training",
      type: "toggle",
      hint: "Required by Safe Work Australia for any work above 2 metres.",
    },
  ],
  Landscaping: [
    {
      key: "landscaper_licence",
      label: "Landscaping contractor licence",
      hint: "Optional in most states — check your state licensing authority.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required if any construction work is involved.",
    },
  ],
  HVAC: [
    {
      key: "hvac_licence_number",
      label: "HVAC contractor licence number",
      hint: "Required in most states for HVAC installation and servicing.",
    },
    {
      key: "arc_tick_licence",
      label: "ARC Tick licence number",
      placeholder: "e.g. AU12345",
      required: true,
      hint: "Nationally required by the Australian Refrigeration Council to handle refrigerants.",
    },
    {
      key: "restricted_electrical_hvac",
      label: "I hold a restricted electrical licence for AC connections",
      type: "toggle",
      hint: "Required to connect split-system air conditioners to mains power.",
    },
  ],
  Handyman: [], // info banners only — no licence fields
  Concreter: [
    {
      key: "concreter_licence",
      label: "Concreting contractor licence",
      hint: "Required in some states — check your state licensing authority.",
    },
    {
      key: "white_card_number",
      label: "White card number",
      hint: "Required for any work on a construction site.",
    },
  ],
};

// ─── Section heading with orange left-border accent ────────────────────────
function SectionHeading({ title, subtitle }) {
  return (
    <div
      style={{
        borderLeft: "4px solid var(--theme-accent)",
        paddingLeft: "14px",
        marginBottom: "24px",
      }}
    >
      <h3 style={{ margin: 0 }}>{title}</h3>
      {subtitle && (
        <p className="muted small" style={{ margin: "3px 0 0" }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─── Toggle switch ─────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label, hint }) {
  return (
    <div style={{ padding: "10px 0" }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "13px",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span
          onClick={(e) => {
            e.preventDefault();
            onChange(!checked);
          }}
          role="switch"
          aria-checked={checked}
          style={{
            position: "relative",
            width: "42px",
            height: "24px",
            background: checked ? "var(--primary)" : "rgba(0,0,0,0.13)",
            borderRadius: "999px",
            transition: "background 0.2s",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: "3px",
              left: checked ? "21px" : "3px",
              width: "18px",
              height: "18px",
              background: "#fff",
              borderRadius: "50%",
              transition: "left 0.2s",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
            }}
          />
        </span>
        <span style={{ fontSize: "0.9rem" }}>{label}</span>
      </label>
      {hint && (
        <p className="muted small" style={{ margin: "4px 0 0 55px" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

// ─── ABN validation ────────────────────────────────────────────────────────
function isValidAbn(abn) {
  if (!abn) return true;
  const digits = String(abn).replace(/\s+/g, "");
  return /^\d{11}$/.test(digits);
}

// ─── Chevron icon (up/down) ────────────────────────────────────────────────
function Chevron({ open }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points={open ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
    </svg>
  );
}

// ─── Info banner ───────────────────────────────────────────────────────────
function InfoBanner({ children }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        alignItems: "flex-start",
        padding: "12px 16px",
        background: "rgba(59,130,246,0.07)",
        border: "1px solid rgba(59,130,246,0.22)",
        borderRadius: "12px",
        marginBottom: "14px",
        fontSize: "0.875rem",
        color: "var(--text, #1C1C1E)",
        lineHeight: 1.5,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: "1px" }}>ℹ️</span>
      <span>{children}</span>
    </div>
  );
}

// ─── Completion fields ─────────────────────────────────────────────────────
const REQUIRED_PROFILE_ITEMS = [
  { key: "full_name", label: "Your name" },
  { key: "phone", label: "Phone number" },
  { key: "business_name", label: "Business name" },
  { key: "abn", label: "ABN" },
  { key: "trade_types", label: "Trade type" },
];

const TRUST_PROFILE_ITEMS = [
  { key: "logo_url", label: "Logo" },
  { key: "business_address", label: "Business address" },
  { key: "website", label: "Website" },
  { key: "years_in_business", label: "Years in business" },
  { key: "insurance_provider", label: "Insurance provider" },
  { key: "insurance_policy_number", label: "Insurance policy number" },
  { key: "insurance_expiry", label: "Insurance expiry" },
  { key: "public_liability_amount", label: "Public liability amount" },
];

function hasValue(form, key) {
  const value = form[key];
  if (Array.isArray(value)) return value.length > 0;
  return String(value ?? "").trim().length > 0;
}

function calcCompletion(form) {
  const requiredDone = REQUIRED_PROFILE_ITEMS.filter((item) => hasValue(form, item.key));
  const trustDone = TRUST_PROFILE_ITEMS.filter((item) => hasValue(form, item.key));
  const missingRequired = REQUIRED_PROFILE_ITEMS.filter((item) => !hasValue(form, item.key));

  return {
    requiredFilled: requiredDone.length,
    requiredTotal: REQUIRED_PROFILE_ITEMS.length,
    requiredPct: Math.round((requiredDone.length / REQUIRED_PROFILE_ITEMS.length) * 100),
    trustFilled: trustDone.length,
    trustTotal: TRUST_PROFILE_ITEMS.length,
    trustPct: Math.round((trustDone.length / TRUST_PROFILE_ITEMS.length) * 100),
    missingRequired,
    ready: missingRequired.length === 0,
  };
}

// ─── Default quote items helpers ───────────────────────────────────────────
// Strip rows where both fields are empty — saves storing useless blank rows
// and keeps the QuoteBuilder pre-populate logic simple.
function cleanDefaultItems(items) {
  return (items || [])
    .map((item) => ({
      description: (item.description || "").trim(),
      amount: item.amount === "" || item.amount === null || item.amount === undefined
        ? 0
        : Number(item.amount) || 0,
    }))
    .filter((item) => item.description !== "" || item.amount !== 0);
}

// ─── Required asterisk ─────────────────────────────────────────────────────
function Req() {
  return (
    <span style={{ color: "#dc2626", marginLeft: "3px" }} aria-label="required">
      *
    </span>
  );
}

// ─── Notification-sound mute toggle row ────────────────────────────────────
// Persists the user's choice to localStorage under "rq_notif_sound_muted".
// Reading the same flag elsewhere drives whether the in-app beep plays.
function NotificationSoundRow() {
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem("rq_notif_sound_muted") === "1";
    } catch {
      return false;
    }
  });

  function toggle() {
    setMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("rq_notif_sound_muted", next ? "1" : "0");
      } catch {}
      // Play a sample chirp when (re-)enabling so the user can hear it.
      if (!next) {
        try {
          // eslint-disable-next-line global-require
          const { playNotificationSound } = require("../../utils/notificationSound");
          playNotificationSound({ force: true });
        } catch {}
      }
      return next;
    });
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "20px",
        padding: "8px 0",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        <p style={{ margin: "0 0 4px", fontWeight: 600 }}>
          {muted ? "Sounds muted" : "Sounds on"}
        </p>
        <p className="muted small" style={{ margin: 0 }}>
          A short chime plays when you receive a new lead, message, or quote acceptance.
        </p>
      </div>
      <Button onClick={toggle} secondary={muted}>
        {muted ? "Unmute" : "Mute"}
      </Button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
export default function TradieSettings({ session, profile, onProfileUpdated, onOpenGodMode }) {
  const { showToast } = useToast();
  const userId = session?.user?.id;
  const userEmail = session?.user?.email || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Dirty-state tracking — save bar only mounts once the user has changed
  // something. Reset to false after a successful save (and after the
  // initial form-from-profile sync via the formInitialized ref guard).
  const [dirty, setDirty] = useState(false);
  const formInitialized = useRef(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [dangerOpen, setDangerOpen] = useState(false);

  const [form, setForm] = useState({
    // Profile
    full_name: "",
    phone: "",
    logo_url: "",
    // Business
    business_name: "",
    abn: "",
    business_address: "",
    website: "",
    trade_types: [],
    years_in_business: "",
    // Universal compliance
    has_insurance: false,
    insurance_provider: "",
    insurance_policy_number: "",
    insurance_expiry: "",
    public_liability_amount: "",
    white_card_number: "",
    gst_registered: false,
    // Electrical
    electrical_contractor_licence: "",
    electrical_worker_licence: "",
    electrical_licence_class: "",
    issues_electrical_safety_cert: false,
    // Plumbing
    plumbing_contractor_licence: "",
    plumbing_licence_categories: "",
    gas_fitting_licence: "",
    issues_compliance_cert: false,
    // Building
    builder_licence_number: "",
    builder_licence_class: "",
    home_warranty_insurance: false,
    // Painting
    painter_licence_number: "",
    // Roofing
    roofer_licence_number: "",
    height_safety_training: false,
    // Landscaping
    landscaper_licence: "",
    // HVAC
    hvac_licence_number: "",
    arc_tick_licence: "",
    restricted_electrical_hvac: false,
    // Concreter
    concreter_licence: "",
    // Notifications
    notify_new_lead: true,
    notify_quote_accepted: true,
    notify_quote_rejected: true,
    // Account
    accept_marketing: false,
    // Quote defaults — pre-populated into the quote builder for new leads.
    // Three empty rows by default so the placeholders guide the tradie on
    // first use; rows can be added or removed below.
    default_quote_items: [
      { description: "", amount: "" },
      { description: "", amount: "" },
      { description: "", amount: "" },
    ],
    default_quote_notes: "",
  });

  // Watch form changes — flip dirty true after the initial profile sync.
  useEffect(() => {
    if (!formInitialized.current) {
      // Skip until the loader effect flips formInitialized true.
      return;
    }
    setDirty(true);
  }, [form]);

  const [savingDefaults, setSavingDefaults] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [showPwdCurrent, setShowPwdCurrent] = useState(false);
  const [showPwdNext, setShowPwdNext] = useState(false);
  const [showPwdConfirm, setShowPwdConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Load profile
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!userId) return;
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        showToast("Failed to load profile.", "error");
      } else if (data) {
        setForm((prev) => ({
          ...prev,
          full_name: data.full_name || "",
          phone: data.phone || "",
          logo_url: data.logo_url || "",
          business_name: data.business_name || "",
          abn: data.abn || "",
          business_address: data.business_address || "",
          website: data.website || "",
          trade_types: Array.isArray(data.trade_types) ? data.trade_types : [],
          years_in_business: data.years_in_business ?? "",
          // Universal compliance
          has_insurance: !!data.has_insurance,
          insurance_provider: data.insurance_provider || "",
          insurance_policy_number: data.insurance_policy_number || "",
          insurance_expiry: data.insurance_expiry || "",
          public_liability_amount: data.public_liability_amount || "",
          white_card_number: data.white_card_number || "",
          gst_registered: !!data.gst_registered,
          // Electrical
          electrical_contractor_licence: data.electrical_contractor_licence || "",
          electrical_worker_licence: data.electrical_worker_licence || "",
          electrical_licence_class: data.electrical_licence_class || "",
          issues_electrical_safety_cert: !!data.issues_electrical_safety_cert,
          // Plumbing
          plumbing_contractor_licence: data.plumbing_contractor_licence || "",
          plumbing_licence_categories: data.plumbing_licence_categories || "",
          gas_fitting_licence: data.gas_fitting_licence || "",
          issues_compliance_cert: !!data.issues_compliance_cert,
          // Building
          builder_licence_number: data.builder_licence_number || "",
          builder_licence_class: data.builder_licence_class || "",
          home_warranty_insurance: !!data.home_warranty_insurance,
          // Painting
          painter_licence_number: data.painter_licence_number || "",
          // Roofing
          roofer_licence_number: data.roofer_licence_number || "",
          height_safety_training: !!data.height_safety_training,
          // Landscaping
          landscaper_licence: data.landscaper_licence || "",
          // HVAC
          hvac_licence_number: data.hvac_licence_number || "",
          arc_tick_licence: data.arc_tick_licence || "",
          restricted_electrical_hvac: !!data.restricted_electrical_hvac,
          // Concreter
          concreter_licence: data.concreter_licence || "",
          // Notifications
          notify_new_lead:
            data.notify_new_lead === undefined ? true : !!data.notify_new_lead,
          notify_quote_accepted:
            data.notify_quote_accepted === undefined
              ? true
              : !!data.notify_quote_accepted,
          notify_quote_rejected:
            data.notify_quote_rejected === undefined
              ? true
              : !!data.notify_quote_rejected,
          // Account
          accept_marketing: !!data.accept_marketing,
          // Quote defaults — keep the three empty rows when nothing has been
          // saved yet so the placeholders still guide the tradie.
          default_quote_items:
            Array.isArray(data.default_quote_items) && data.default_quote_items.length > 0
              ? data.default_quote_items.map((item) => ({
                  description: item.description || item.name || "",
                  amount: item.amount ?? "",
                }))
              : [
                  { description: "", amount: "" },
                  { description: "", amount: "" },
                  { description: "", amount: "" },
                ],
          default_quote_notes: data.default_quote_notes ?? "",
        }));
      }
      // After the initial profile -> form sync, allow dirty tracking.
      // setTimeout(0) so the form effect's first run sees the ref still
      // false and skips the dirty flip.
      setTimeout(() => { formInitialized.current = true; }, 0);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId, showToast]);

  // Compute the union of compliance fields for currently selected trades,
  // deduplicating by key so shared fields (e.g. white_card_number) appear once.
  const activeComplianceFields = useMemo(() => {
    const seen = new Set();
    const fields = [];
    for (const trade of form.trade_types) {
      for (const field of TRADE_COMPLIANCE[trade] || []) {
        if (!seen.has(field.key)) {
          seen.add(field.key);
          fields.push(field);
        }
      }
    }
    return fields;
  }, [form.trade_types]);

  const showHandymanBanner = form.trade_types.includes("Handyman");

  const abnValid = useMemo(() => isValidAbn(form.abn), [form.abn]);
  const completion = useMemo(
    () => calcCompletion(form),
    [form]
  );
  const completionPct = completion.requiredPct;

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleTrade(t) {
    setForm((prev) => {
      const has = prev.trade_types.includes(t);
      return {
        ...prev,
        trade_types: has
          ? prev.trade_types.filter((x) => x !== t)
          : [...prev.trade_types, t],
      };
    });
  }

  function updateDefaultItem(index, field, value) {
    setForm((prev) => ({
      ...prev,
      default_quote_items: prev.default_quote_items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  }

  function addDefaultItemRow() {
    setForm((prev) => ({
      ...prev,
      default_quote_items: [
        ...prev.default_quote_items,
        { description: "", amount: "" },
      ],
    }));
  }

  function removeDefaultItemRow(index) {
    setForm((prev) => ({
      ...prev,
      default_quote_items: prev.default_quote_items.filter((_, i) => i !== index),
    }));
  }

  async function handleSaveDefaults() {
    if (!userId) return;
    setSavingDefaults(true);
    const cleaned = cleanDefaultItems(form.default_quote_items);
    const { error } = await supabase
      .from("profiles")
      .update({
        default_quote_items: cleaned,
        default_quote_notes: form.default_quote_notes ?? "",
      })
      .eq("id", userId);
    setSavingDefaults(false);
    if (error) {
      showToast(`Failed to save defaults: ${error.message}`, "error");
    } else {
      showToast("Quote defaults saved.", "success");
      if (onProfileUpdated) onProfileUpdated();
    }
  }

  async function handleSave() {
    if (!userId) return;
    if (!abnValid) {
      showToast("ABN must be 11 digits.", "error");
      return;
    }
    setSaving(true);
    const payload = {
      // Profile
      full_name: form.full_name || null,
      phone: form.phone || null,
      logo_url: form.logo_url || null,
      // Business
      business_name: form.business_name || null,
      abn: form.abn ? form.abn.replace(/\s+/g, "") : null,
      business_address: form.business_address || null,
      website: form.website || null,
      trade_types: form.trade_types,
      years_in_business:
        form.years_in_business === "" ? null : Number(form.years_in_business),
      // Universal compliance
      has_insurance: form.has_insurance,
      insurance_provider: form.insurance_provider || null,
      insurance_policy_number: form.insurance_policy_number || null,
      insurance_expiry: form.insurance_expiry || null,
      public_liability_amount: form.public_liability_amount || null,
      white_card_number: form.white_card_number || null,
      gst_registered: form.gst_registered,
      // Electrical
      electrical_contractor_licence: form.electrical_contractor_licence || null,
      electrical_worker_licence: form.electrical_worker_licence || null,
      electrical_licence_class: form.electrical_licence_class || null,
      issues_electrical_safety_cert: form.issues_electrical_safety_cert,
      // Plumbing
      plumbing_contractor_licence: form.plumbing_contractor_licence || null,
      plumbing_licence_categories: form.plumbing_licence_categories || null,
      gas_fitting_licence: form.gas_fitting_licence || null,
      issues_compliance_cert: form.issues_compliance_cert,
      // Building
      builder_licence_number: form.builder_licence_number || null,
      builder_licence_class: form.builder_licence_class || null,
      home_warranty_insurance: form.home_warranty_insurance,
      // Painting
      painter_licence_number: form.painter_licence_number || null,
      // Roofing
      roofer_licence_number: form.roofer_licence_number || null,
      height_safety_training: form.height_safety_training,
      // Landscaping
      landscaper_licence: form.landscaper_licence || null,
      // HVAC
      hvac_licence_number: form.hvac_licence_number || null,
      arc_tick_licence: form.arc_tick_licence || null,
      restricted_electrical_hvac: form.restricted_electrical_hvac,
      // Concreter
      concreter_licence: form.concreter_licence || null,
      // Notifications
      notify_new_lead: form.notify_new_lead,
      notify_quote_accepted: form.notify_quote_accepted,
      notify_quote_rejected: form.notify_quote_rejected,
      // Account
      accept_marketing: form.accept_marketing,
      // Quote defaults — strip rows that are completely empty so we don't
      // pre-populate blank lines into the quote builder.
      default_quote_items: cleanDefaultItems(form.default_quote_items),
    };
    const { error } = await supabase
      .from("profiles")
      .update(payload)
      .eq("id", userId);
    setSaving(false);
    if (error) {
      showToast(`Save failed: ${error.message}`, "error");
    } else {
      showToast("Settings saved successfully.", "success");
      setDirty(false);
      if (onProfileUpdated) onProfileUpdated();
    }
  }

  async function handleChangePassword() {
    if (!pwd.next || pwd.next.length < 6) {
      showToast("New password must be at least 6 characters.", "error");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      showToast("Passwords do not match.", "error");
      return;
    }
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: userEmail,
      password: pwd.current,
    });
    if (signInErr) {
      showToast("Current password is incorrect.", "error");
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: pwd.next });
    if (error) {
      showToast(`Password change failed: ${friendlyAuthError(error.message)}`, "error");
    } else {
      showToast("Password updated.", "success");
      setPwd({ current: "", next: "", confirm: "" });
    }
  }

  async function handleDeleteAccount() {
    if (deleteText !== "DELETE") {
      showToast('Type "DELETE" to confirm.', "error");
      return;
    }
    setDeleting(true);
    const { error } = await supabase
      .from("profiles")
      .delete()
      .eq("id", userId);
    if (error) {
      showToast(`Delete failed: ${error.message}`, "error");
      setDeleting(false);
      return;
    }
    showToast("Account deleted. Signing out…", "success");
    await supabase.auth.signOut();
    setDeleting(false);
  }

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Card>
        <h2>Settings</h2>
        <p className="muted">Loading your profile…</p>
      </Card>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: "860px", margin: "0 auto", paddingBottom: "90px" }}>

      {/* ── Settings tab bar ─────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          marginBottom: "20px",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          paddingBottom: "4px",
        }}
        role="tablist"
        aria-label="Settings sections"
      >
        {[
          { id: "profile",       label: "Profile"       },
          { id: "how",           label: "How it works"  },
          { id: "business",      label: "Business"      },
          { id: "compliance",    label: "Compliance"    },
          { id: "templates",     label: "Templates"     },
          { id: "branding",      label: "Branding"      },
          { id: "notifications", label: "Notifications" },
          { id: "account",       label: "Account"       },
          ...(onOpenGodMode ? [{ id: "admin", label: "Admin" }] : []),
        ].map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTab(t.id)}
              style={{
                background: active ? "var(--primary)" : "transparent",
                color: active ? "#fff" : "var(--text-soft, #6b5448)",
                border: active
                  ? "1px solid var(--primary)"
                  : "1px solid var(--border, rgba(140,100,80,0.18))",
                borderRadius: "999px",
                padding: "8px 16px",
                fontSize: "0.85rem",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background 0.18s ease, color 0.18s ease, transform 0.12s ease",
                transform: active ? "translateY(-1px)" : "none",
                boxShadow: active
                  ? "0 6px 14px rgba(255, 138, 0,0.28)"
                  : "none",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Progress indicator */}
      <div style={{ marginBottom: "28px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: "10px",
          }}
        >
          <h2 style={{ margin: 0 }}>Profile Settings</h2>
          <span
            style={{
              fontSize: "0.875rem",
              fontWeight: 700,
              color: completion.ready ? "#22C55E" : "var(--primary)",
            }}
          >
            {completion.ready ? "Ready to quote" : `${completionPct}% ready`}
          </span>
        </div>
        <div
          style={{
            height: "7px",
            background: "rgba(0,0,0,0.08)",
            borderRadius: "999px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${completionPct}%`,
              background: completion.ready ? "#22C55E" : "var(--theme-primary-gradient)",
              borderRadius: "999px",
              transition: "width 0.5s ease",
            }}
          />
        </div>
        <p className="muted small" style={{ margin: "8px 0 0" }}>
          {completion.ready
            ? `Required basics are done. Trust boosters ${completion.trustFilled}/${completion.trustTotal}. Leave optional fields blank if they do not apply.`
            : `Add ${completion.missingRequired.map((item) => item.label).join(", ")} to be ready to quote.`}
        </p>
        <p className="muted small" style={{ margin: "4px 0 0" }}>
          Optional trust boosters are not blockers. They just help customers feel safer choosing you.
        </p>
      </div>
      {["profile","business","compliance","notifications","account"].includes(activeTab) && (
        <>
      {/* ── Section 1 — Your profile ───────────────────────────────────── */}
      {activeTab === "profile" && (
      <div style={{ marginBottom: "20px" }}>
        <div className="card">
          <SectionHeading
            title="Your profile"
            subtitle="Visible to customers on quotes and your direct link"
          />

          <div style={GRID}>
            <div>
              <label>Full name</label>
              <input
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <div>
              <label>Phone number</label>
              <input
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="04xx xxx xxx"
              />
            </div>
            <div>
              <label>Email address</label>
              <input
                value={userEmail}
                readOnly
                style={{ opacity: 0.55, cursor: "not-allowed" }}
              />
              <p className="muted small" style={{ margin: "6px 0 0" }}>
                Email is managed via your account
              </p>
            </div>
          </div>

          {/* Business logo */}
          <div style={{ ...DIVIDER }} />
          <label>Business logo</label>
          <LogoUpload
            userId={userId}
            logoUrl={form.logo_url}
            onUpload={(url) => update("logo_url", url)}
            size={88}
            layout="row"
            hint="JPG, PNG or WebP · Square works best · Max 2 MB"
          />
        </div>
      </div>

)}

      {/* ── Section 2 — Your business ──────────────────────────────────── */}
      {activeTab === "business" && (
      <div style={{ marginBottom: "20px" }}>
        <div className="card">
          <SectionHeading
            title="Your business"
            subtitle="Used on quotes, invoices and your public profile"
          />

          <div style={GRID}>
            <div>
              <label>Business name</label>
              <input
                value={form.business_name}
                onChange={(e) => update("business_name", e.target.value)}
                placeholder="e.g. Smith Painting Co."
              />
            </div>
            <div>
              <label>
                ABN{" "}
                <span style={{ fontWeight: 500, color: "var(--text-faint, #a8897c)", fontSize: "0.85em" }}>
                  (11 digits)
                </span>
              </label>
              <input
                value={form.abn}
                onChange={(e) => update("abn", e.target.value)}
                placeholder="12 345 678 901"
                style={abnValid ? {} : { borderColor: "#dc2626" }}
              />
              {!abnValid && (
                <p style={{ margin: "5px 0 0", fontSize: "0.8rem", color: "#dc2626" }}>
                  Must be exactly 11 digits
                </p>
              )}
              <p className="muted small" style={{ margin: "6px 0 0", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <a href="https://abr.business.gov.au/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-soft, #6b7280)" }}>Look up my ABN</a>
                <a href="https://www.ato.gov.au/businesses-and-organisations/preparing-for-business/setting-up-your-business/abn" target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-soft, #6b7280)" }}>Don't have an ABN? Register with the ATO</a>
              </p>
            </div>
            <div>
              <label>Years in business</label>
              <input
                type="number"
                min="0"
                value={form.years_in_business}
                onChange={(e) => update("years_in_business", e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label>Website URL</label>
              <input
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
                placeholder="https://"
              />
            </div>
            <div>
              <label>Business address</label>
              <input
                value={form.business_address}
                onChange={(e) => update("business_address", e.target.value)}
                placeholder="Your business street address"
              />
            </div>
          </div>

          {/* Trade type pills */}
          <div style={{ ...DIVIDER }} />
          <label>
            Trade types{" "}
            <span style={{ fontWeight: 500, color: "var(--text-faint, #a8897c)", fontSize: "0.85em" }}>
              — select all that apply
            </span>
          </label>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginTop: "4px",
            }}
          >
            {TRADE_TYPES.map((t) => {
              const active = form.trade_types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTrade(t)}
                  style={{
                    padding: "10px 22px",
                    minHeight: "44px",
                    borderRadius: "999px",
                    border: active
                      ? "1.5px solid #FF8A00"
                      : "1.5px solid rgba(140,100,80,0.22)",
                    background: active
                      ? "linear-gradient(135deg, #CC6E00 0%, #FF8A00 60%, #FFD27A 100%)"
                      : "rgba(255,255,255,0.76)",
                    color: active ? "#fff" : "var(--text, #1C1C1E)",
                    fontSize: "0.9rem",
                    fontWeight: active ? 750 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: active
                      ? "0 4px 14px rgba(255, 138, 0,0.28)"
                      : "var(--shadow-xs)",
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

)}

      {/* ── Section 3 — Compliance & licencing ────────────────────────── */}
      {activeTab === "compliance" && (
      <>
      <div style={{ marginBottom: "20px" }}>
        <div className="card">
          <SectionHeading
            title="Compliance & licencing"
            subtitle="Builds customer trust and may be required for certain job types"
          />

          {/* State disclaimer banner */}
          <InfoBanner>
            Requirements vary by state. Make sure your licences are current and
            valid in your operating state. Fields marked <Req /> are mandatory
            for that trade.
          </InfoBanner>

          {/* Handyman restriction banners */}
          {showHandymanBanner && (
            <>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  padding: "12px 16px",
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.35)",
                  borderRadius: "12px",
                  marginBottom: "10px",
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                }}
              >
                <span style={{ flexShrink: 0 }}>⚠️</span>
                <span>
                  <strong>Handyman work must not exceed $5,000 in total value</strong> per
                  job (threshold varies by state — check your state licensing authority).
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  padding: "12px 16px",
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.35)",
                  borderRadius: "12px",
                  marginBottom: "14px",
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                }}
              >
                <span style={{ flexShrink: 0 }}>⚠️</span>
                <span>
                  <strong>No structural, electrical or plumbing work is permitted</strong> under
                  a handyman arrangement — these require a licensed tradesperson.
                </span>
              </div>
            </>
          )}

          {/* No trades selected placeholder */}
          {form.trade_types.length === 0 && (
            <p className="muted small" style={{ marginBottom: "20px" }}>
              Select your trade types above to see the relevant compliance fields for your licence.
            </p>
          )}

          {/* Trade-specific fields (union, deduped) */}
          {activeComplianceFields.length > 0 && (
            <>
              <div style={GRID}>
                {activeComplianceFields
                  .filter((f) => f.type !== "toggle")
                  .map((field) => (
                    <div key={field.key}>
                      <label>
                        {field.label}
                        {field.required && <Req />}
                      </label>
                      <input
                        type={field.type === "date" ? "date" : "text"}
                        value={form[field.key] || ""}
                        onChange={(e) => update(field.key, e.target.value)}
                        placeholder={field.placeholder || ""}
                      />
                      {field.hint && (
                        <p className="muted small" style={{ margin: "5px 0 0" }}>
                          {field.hint}
                        </p>
                      )}
                    </div>
                  ))}
              </div>

              {/* Toggle fields from trade-specific set */}
              {activeComplianceFields
                .filter((f) => f.type === "toggle")
                .map((field) => (
                  <Toggle
                    key={field.key}
                    checked={!!form[field.key]}
                    onChange={(v) => update(field.key, v)}
                    label={field.label}
                    hint={field.hint}
                  />
                ))}

              <div style={DIVIDER} />
            </>
          )}

          {/* Insurance toggle */}
          <Toggle
            checked={form.has_insurance}
            onChange={(v) => update("has_insurance", v)}
            label="I have public liability insurance"
            hint="Required for most jobs — protects you and your clients."
          />

          {form.has_insurance ? (
            <div style={{ marginTop: "4px", marginBottom: "8px" }}>
              <div style={GRID}>
                <div>
                  <label>Insurance provider</label>
                  <input
                    value={form.insurance_provider}
                    onChange={(e) => update("insurance_provider", e.target.value)}
                    placeholder="e.g. Aon, CGU, Allianz"
                  />
                </div>
                <div>
                  <label>Policy number</label>
                  <input
                    value={form.insurance_policy_number}
                    onChange={(e) => update("insurance_policy_number", e.target.value)}
                  />
                </div>
                <div>
                  <label>Insurance expiry date</label>
                  <input
                    type="date"
                    value={form.insurance_expiry || ""}
                    onChange={(e) => update("insurance_expiry", e.target.value)}
                  />
                  <p className="muted small" style={{ margin: "5px 0 0" }}>
                    Keep this current — expired insurance is the same as no insurance.
                  </p>
                </div>
                <div>
                  <label>Public liability amount</label>
                  <input
                    value={form.public_liability_amount}
                    onChange={(e) => update("public_liability_amount", e.target.value)}
                    placeholder="e.g. $5,000,000"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "flex-start",
                padding: "10px 14px",
                background: "rgba(245,158,11,0.07)",
                border: "1px solid rgba(245,158,11,0.28)",
                borderRadius: "12px",
                marginTop: "4px",
                marginBottom: "8px",
                fontSize: "0.85rem",
                color: "var(--text, #1C1C1E)",
                lineHeight: 1.5,
              }}
            >
              <span style={{ flexShrink: 0 }}>⚠️</span>
              <span>
                Most customers and platforms require a minimum of{" "}
                <strong>$5M public liability cover</strong>.
              </span>
            </div>
          )}

          <div style={DIVIDER} />
          <Toggle
            checked={form.gst_registered}
            onChange={(v) => update("gst_registered", v)}
            label="GST registered"
            hint="Required once your annual turnover exceeds $75,000."
          />
        </div>
      </div>
      </>
      )}

      {/* ── Section 4 — Notifications ──────────────────────────────────── */}
      {activeTab === "notifications" && (
      <div style={{ marginBottom: "20px" }}>
        <div className="card">
          <SectionHeading
            title="Notifications"
            subtitle="Choose when we send you an email alert"
          />
          <Toggle
            checked={form.notify_new_lead}
            onChange={(v) => update("notify_new_lead", v)}
            label="Email me when a new lead arrives"
          />
          <Toggle
            checked={form.notify_quote_accepted}
            onChange={(v) => update("notify_quote_accepted", v)}
            label="Email me when a customer accepts my quote"
          />
          <Toggle
            checked={form.notify_quote_rejected}
            onChange={(v) => update("notify_quote_rejected", v)}
            label="Email me when a customer rejects my quote"
          />
        </div>
      </div>

)}

      {/* ── Section 5 — Account & security ────────────────────────────── */}
      {activeTab === "account" && (
      <div style={{ marginBottom: "20px" }}>
        <div className="card">
          <SectionHeading
            title="Account & security"
            subtitle="Manage your password, preferences and account"
          />

          {/* Change password */}
          <div style={{ marginBottom: "16px" }}>
            <label>Current password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPwdCurrent ? "text" : "password"}
                value={pwd.current}
                onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPwdCurrent((v) => !v)}
                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                tabIndex={-1}
                aria-label={showPwdCurrent ? "Hide password" : "Show password"}
              >
                {showPwdCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div style={GRID}>
            <div>
              <label>New password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPwdNext ? "text" : "password"}
                  value={pwd.next}
                  onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPwdNext((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showPwdNext ? "Hide password" : "Show password"}
                >
                  {showPwdNext ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label>Confirm new password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPwdConfirm ? "text" : "password"}
                  value={pwd.confirm}
                  onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPwdConfirm((v) => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--muted, #6b7280)", display: "flex", alignItems: "center" }}
                  tabIndex={-1}
                  aria-label={showPwdConfirm ? "Hide password" : "Show password"}
                >
                  {showPwdConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "14px" }}>
            <Button secondary onClick={handleChangePassword}>
              Change password
            </Button>
          </div>

          <div style={DIVIDER} />

          {/* Marketing + legal */}
          <Toggle
            checked={form.accept_marketing}
            onChange={(v) => update("accept_marketing", v)}
            label="Accept marketing emails from Remote Quote"
          />
          <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--primary)", fontSize: "0.875rem" }}
            >
              Terms of Service
            </a>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--primary)", fontSize: "0.875rem" }}
            >
              Privacy Policy
            </a>
          </div>

          {/* ── Collapsible danger zone ───────────────────────────────── */}
          <div style={{ marginTop: "28px" }}>
            <button
              type="button"
              onClick={() => setDangerOpen((o) => !o)}
              style={{
                background: "none",
                border: "none",
                color: "#dc2626",
                fontSize: "0.875rem",
                fontWeight: 700,
                cursor: "pointer",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Chevron open={dangerOpen} />
              {dangerOpen ? "Hide" : "Delete my account"}
            </button>

            {dangerOpen && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "18px 20px",
                  border: "1px solid #fecaca",
                  background: "rgba(220,38,38,0.04)",
                  borderRadius: "14px",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: "#dc2626",
                    marginBottom: "8px",
                    fontSize: "0.95rem",
                  }}
                >
                  ⚠️ Danger zone
                </div>
                <p className="muted small" style={{ margin: "0 0 14px" }}>
                  Deleting your account is permanent — all your profile data
                  will be removed. Type <strong>DELETE</strong> to confirm.
                </p>
                <input
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  style={{ marginBottom: "12px", borderColor: "#fca5a5" }}
                />
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteText !== "DELETE"}
                  style={{
                    padding: "10px 22px",
                    borderRadius: "12px",
                    border: "none",
                    background:
                      deleteText === "DELETE" ? "#dc2626" : "#fca5a5",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    cursor:
                      deleteText === "DELETE" && !deleting
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  {deleting ? "Deleting…" : "Delete account"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

        </>
      )}

      {/* ── Templates tab — quote templates + per-tradie defaults ──── */}
      {activeTab === "how" && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <SectionHeading
            title="How Remote Quote works"
            subtitle="A quick refresher for quoting jobs from your direct link"
          />
          <HowItWorksPanel />
        </div>
      )}

      {activeTab === "templates" && (
        <>
          <div className="card" style={{ marginBottom: "20px" }}>
            <SectionHeading
              title="Quote templates"
              subtitle="Save reusable line items so every new quote starts with one tap"
            />
            <QuoteTemplatesPanel businessId={profile?.business_id} profile={profile} />
          </div>

          <div className="card" style={{ marginBottom: "20px" }}>
            <SectionHeading
              title="Default line items"
              subtitle="Pre-fill every new quote with your most common charges. Edit or remove per quote."
            />

            {(form.default_quote_items || []).map((item, index) => {
              const placeholders = [
                { name: "e.g. Labour", amount: "0" },
                { name: "e.g. Materials", amount: "0" },
                { name: "e.g. Travel", amount: "0" },
              ];
              const ph = placeholders[index] || { name: "Item name", amount: "0" };
              return (
                <div
                  key={index}
                  className="line-item-row"
                  style={{ marginBottom: "8px" }}
                >
                  <input
                    placeholder={ph.name}
                    value={item.description}
                    onChange={(e) =>
                      updateDefaultItem(index, "description", e.target.value)
                    }
                  />
                  <input
                    placeholder={ph.amount}
                    value={item.amount}
                    onChange={(e) =>
                      updateDefaultItem(index, "amount", e.target.value)
                    }
                  />
                  <Button
                    secondary
                    onClick={() => removeDefaultItemRow(index)}
                  >
                    Remove
                  </Button>
                </div>
              );
            })}

            <div style={{ marginTop: "16px" }}>
              <label>Default notes for customer</label>
              <p className="muted small" style={{ margin: "2px 0 6px" }}>
                Pre-fills the "Quote notes / assumptions" field on every new quote. Edit per-quote if needed.
              </p>
              <textarea
                rows={4}
                value={form.default_quote_notes}
                onChange={(e) => update("default_quote_notes", e.target.value)}
                placeholder="e.g. Quote valid for 30 days. 50% deposit required to book job."
              />
            </div>

            <div className="button-row" style={{ marginTop: "8px" }}>
              <Button secondary onClick={addDefaultItemRow}>
                + Add default item
              </Button>
              <Button onClick={handleSaveDefaults} disabled={savingDefaults}>
                {savingDefaults ? "Saving…" : "Save defaults"}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ── Branding tab ──────────────────────────────────────────────── */}
      {activeTab === "branding" && (
        <>
          <div className="card" style={{ marginBottom: "20px" }}>
            <SectionHeading
              title="Direct-link customisation"
              subtitle="Tailor the public quote-request page customers see when they tap your link"
            />
            <DirectLinkConfigPanel businessId={profile?.business_id} />
          </div>

          <div className="card" style={{ marginBottom: "20px" }}>
            <SectionHeading
              title="More branding"
              subtitle="Logo + email signature customisation"
            />
            <div
              style={{
                padding: "20px 0",
                textAlign: "center",
                color: "var(--text-soft, #6b5448)",
              }}
            >
              <div style={{ fontSize: "2.2rem", marginBottom: "8px" }}>🎨</div>
              <p style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--text)" }}>
                Coming soon
              </p>
              <p className="muted small" style={{ margin: 0, maxWidth: "440px", marginInline: "auto" }}>
                Custom accent colour, custom email signature. Right now your logo
                from the Profile tab carries through to quotes, invoices, and your
                direct link.
              </p>
            </div>
          </div>
        </>
      )}

      {/* ── Notifications tab — sounds + push extras ───────────────── */}
      {activeTab === "notifications" && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <SectionHeading
            title="In-app sounds"
            subtitle="A short chime plays for new messages, quote acceptance, and other alerts"
          />
          <NotificationSoundRow />
        </div>
      )}

      {/* ── Account tab — appearance toggle alongside auth/danger zone ─ */}
      {activeTab === "account" && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <SectionHeading
            title="Appearance"
            subtitle="Switch between dark and light mode (saved on this device)"
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "20px",
              padding: "8px 0",
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              <p style={{ margin: "0 0 4px", fontWeight: 600 }}>Theme</p>
              <p className="muted small" style={{ margin: 0 }}>
                Dark mode is on by default.
              </p>
            </div>
            <ThemeToggle />
          </div>
        </div>
      )}

      {/* ── Admin tab — god-mode entry point (gated by onOpenGodMode) ── */}
      {activeTab === "admin" && onOpenGodMode && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <SectionHeading
            title="God Mode"
            subtitle="Admin tools — only visible to Remote Quote staff"
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "20px",
              padding: "8px 0",
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              <p style={{ margin: "0 0 4px", fontWeight: 600 }}>Open admin panel</p>
              <p className="muted small" style={{ margin: 0 }}>
                Simulate tiers, add credits, manage god-mode users.
              </p>
            </div>
            <Button onClick={onOpenGodMode}>⚡ Open God Mode</Button>
          </div>
        </div>
      )}

      {/* ── Floating save bar — only mounts when the form is dirty ─── */}
      {dirty && (
        <div className="rq-save-bar" role="status" aria-live="polite">
          <span className="rq-save-bar__hint">
            {completion.ready
              ? "Ready to quote. Saving will apply your latest edits."
              : "Required basics still need attention. Unsaved changes pending."}
          </span>
          <Button onClick={handleSave} disabled={saving || !abnValid}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
